import { useEffect, type RefObject } from 'react';

/**
 * Dismiss a disclosure when the pointer goes down anywhere outside it.
 *
 * The companion to `useEscapeClose`: keyboard users close a panel with Escape, touch users close it
 * by tapping the page. Without this a phone has no way out of an open panel short of choosing one
 * of its items.
 *
 * `pointerdown` rather than `click`: it fires before focus moves and covers touch, pen and mouse in
 * one listener. It is registered in the CAPTURE phase so a panel item that stops propagation cannot
 * strand the panel open, and the `contains` check still exempts the panel's own subtree.
 *
 * The trigger must live inside `ref` too — otherwise the button's own pointerdown closes the panel
 * and its click immediately reopens it.
 */
export function useOutsideDismiss(
  ref: RefObject<HTMLElement | null>,
  onDismiss: () => void,
  isOpen: boolean,
): void {
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: PointerEvent) => {
      const root = ref.current;
      if (root && !root.contains(e.target as Node)) onDismiss();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isOpen, onDismiss, ref]);
}
